import {
  useEffect,
  useMemo,
  useState
} from 'react'
import {
  useParams
} from 'react-router-dom'
import {
  format
} from 'date-fns'
import {
  Calendar,
  CalendarClock,
  CheckCircle2,
  Clock,
  Globe,
  Plus,
  Trash2
} from 'lucide-react'
import {
  getConciergeThreadContextApiV1PublicConciergeTokenGet,
  submitConciergeAvailabilityApiV1PublicConciergeTokenAvailabilityPost
} from '../client'
import type {
  AvailabilityWindow,
  ConciergeThreadContext
} from '../client'
import {
  API_BASE_URL
} from '../config'
import {
  buildPostHogHashFragment
} from '../analytics/posthog'
import {
  formatTimezoneName
} from './scheduling/schedulingUtils'

// Mirrors CONCIERGE_MAX_AVAILABILITY_WINDOWS on the backend.
const MAX_WINDOWS = 20

// How far out the backend accepts windows (6 months); mirrored here as the
// date inputs' max so the picker can't offer an out-of-range day.
const MAX_HORIZON_DAYS = 180

// Default start-of-window time filled in when the organizer first picks a day,
// so a window is usable after a single tap.
const DEFAULT_START_TIME = '09:00'

const FIELD_CLASS = 'w-full h-12 rounded-xl border border-catch-beige-dark bg-white px-3 '
  + 'text-base text-catch-black shadow-sm transition-colors '
  + 'focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30'

const CATCH_HOME_URL_WITH_UTM = 'https://catchagent.ai?'
  + new URLSearchParams({
    utm_source: 'catch_concierge',
    utm_medium: 'powered_by',
  }).toString()

// FastAPI puts a human-readable string in `detail` for expected failures
// (inactive link, invalid windows); validation errors put a list there.
// Only surface the string form.
function errorDetail(error: unknown): string | null {
  if (error && typeof error === 'object' && 'detail' in error) {
    const detail = (error as { detail?: unknown }).detail
    if (typeof detail === 'string') {
      return detail
    }
  }
  return null
}

// An availability window as edited on this page: datetime-local values
// ("YYYY-MM-DDTHH:mm") read as wall time in the selected timezone. They're
// submitted naive — the backend attaches the submitted timezone. The date and
// time halves are edited separately in the UI but stored combined so the
// submit payload stays unchanged.
interface WindowDraft {
  start: string
  end: string
}

function datePart(local: string): string {
  return local.split('T')[0] ?? ''
}

function timePart(local: string): string {
  return local.split('T')[1] ?? ''
}

function combine(date: string, time: string): string {
  if (!date && !time) return ''
  return `${date}T${time}`
}

const COMPLETE_LOCAL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/

function isComplete(local: string): boolean {
  return COMPLETE_LOCAL.test(local)
}

// Add one wall-clock hour to a complete datetime-local value, rolling the date
// across midnight when needed. UTC arithmetic keeps it a pure +1h regardless of
// DST (the value is naive and reinterpreted in the selected zone on submit).
function addOneHour(local: string): string {
  const [y = 0, mo = 1, da = 1] = datePart(local).split('-').map(Number)
  const [h = 0, mi = 0] = timePart(local).split(':').map(Number)
  const plus = new Date(Date.UTC(y, mo - 1, da, h, mi) + 3_600_000)
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${plus.getUTCFullYear()}-${p(plus.getUTCMonth() + 1)}-${p(plus.getUTCDate())}`
    + `T${p(plus.getUTCHours())}:${p(plus.getUTCMinutes())}`
}

// Keep the end sensible when the start changes. Fill an empty end with
// start + 1h, and keep a still-valid end untouched (moving the start earlier
// just widens the window). If the new start lands at/after the end, only
// auto-shift when the end was still the auto-filled +1h of the previous start —
// an end the organizer chose themselves is preserved so the out-of-order note
// gives them feedback instead of silently discarding their value.
function reconcileEnd(previousStart: string, nextStart: string, end: string): string {
  if (!isComplete(nextStart)) return end
  if (!isComplete(end)) return addOneHour(nextStart)
  if (end > nextStart) return end
  if (isComplete(previousStart) && end === addOneHour(previousStart)) {
    return addOneHour(nextStart)
  }
  return end
}

// "YYYY-MM-DDTHH:mm" -> a local Date, parsed component-wise so it is never
// shifted by the runtime's UTC offset. Used only for the friendly summary line.
function parseLocal(local: string): Date | null {
  if (!isComplete(local)) return null
  const [y = 0, mo = 1, da = 1] = datePart(local).split('-').map(Number)
  const [h = 0, mi = 0] = timePart(local).split(':').map(Number)
  return new Date(y, mo - 1, da, h, mi)
}

function formatWindowSummary(start: string, end: string): string | null {
  const s = parseLocal(start)
  const e = parseLocal(end)
  if (!s || !e || e <= s) return null
  if (datePart(start) === datePart(end)) {
    return `${format(s, 'EEE, MMM d')} · ${format(s, 'h:mm a')} – ${format(e, 'h:mm a')}`
  }
  return `${format(s, 'EEE, MMM d, h:mm a')} – ${format(e, 'EEE, MMM d, h:mm a')}`
}

// ISO timestamp (with offset) -> datetime-local input value in `tz`.
// Used to prefill previously submitted windows for editing.
function isoToLocalInput(iso: string, tz: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso))
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`
}

function windowsFromContext(context: ConciergeThreadContext, tz: string): WindowDraft[] {
  if (context.availability_windows.length === 0) {
    return [{ start: '', end: '' }]
  }
  return context.availability_windows.map((w: AvailabilityWindow) => ({
    start: isoToLocalInput(w.start, tz),
    end: isoToLocalInput(w.end, tz),
  }))
}

// datetime-local values are lexicographically ordered, so string comparison
// is enough to check that a window ends after it starts.
function windowIsOrdered(w: WindowDraft): boolean {
  return isComplete(w.start) && isComplete(w.end) && w.end > w.start
}

// Mirrors _MAX_WINDOW_DURATION on the backend (48h). Surfaced client-side so a
// too-long window is caught inline instead of via the generic submit error.
const MAX_WINDOW_HOURS = 48

function windowIsTooLong(w: WindowDraft): boolean {
  const start = parseLocal(w.start)
  const end = parseLocal(w.end)
  if (!start || !end || end <= start) return false
  return (end.getTime() - start.getTime()) / 3_600_000 > MAX_WINDOW_HOURS
}

// Today / horizon as datetime-local date keys, so the native date pickers can't
// offer days the backend would reject.
function localDateKey(date: Date): string {
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`
}

function PoweredByCatch(): JSX.Element {
  return (
    <div className="text-center text-xs text-gray-400 mt-8 pb-4">
      Powered by{' '}
      <a
        href={CATCH_HOME_URL_WITH_UTM + buildPostHogHashFragment()}
        className="font-medium text-primary-500 hover:underline"
      >
        Catch
      </a>
    </div>
  )
}

function StatusCard({ title, showCheck = false, children }: {
  title: string,
  showCheck?: boolean,
  children: React.ReactNode
}): JSX.Element {
  return (
    <div className="min-h-screen flex items-center justify-center bg-catch-white px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-soft-lg border border-catch-beige p-8 text-center animate-scale-in">
          {showCheck ? (
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-500/10">
              <CheckCircle2 className="w-8 h-8 text-primary-500" />
            </div>
          ) : (
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-catch-beige">
              <CalendarClock className="w-7 h-7 text-catch-black/70" />
            </div>
          )}
          <h1 className="font-display text-2xl text-catch-black mb-2">{title}</h1>
          <div className="text-gray-600 leading-relaxed">{children}</div>
        </div>
        <PoweredByCatch />
      </div>
    </div>
  )
}

export function ConciergeAvailabilityPage(): JSX.Element {
  const { token } = useParams<{ token: string }>()

  const [context, setContext] = useState<ConciergeThreadContext | null>(null)
  const [loading, setLoading] = useState(true)
  const [inactive, setInactive] = useState(false)
  const [timezone, setTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone
  )
  const [windows, setWindows] = useState<WindowDraft[]>([{ start: '', end: '' }])
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const allTimezones = useMemo(() => Intl.supportedValuesOf('timeZone'), [])
  // Keep the selected zone selectable even if the runtime's list omits it
  // (e.g. a server-stored zone alias).
  const timezoneOptions = allTimezones.includes(timezone)
    ? allTimezones
    : [timezone, ...allTimezones]

  const { minDateKey, maxDateKey } = useMemo(() => {
    const today = new Date()
    const horizon = new Date(today)
    horizon.setDate(horizon.getDate() + MAX_HORIZON_DAYS)
    return { minDateKey: localDateKey(today), maxDateKey: localDateKey(horizon) }
  }, [])

  useEffect(() => {
    if (!token) return
    getConciergeThreadContextApiV1PublicConciergeTokenGet({
      path: { token },
      baseURL: API_BASE_URL,
      throwOnError: false,
    })
      .then(({ data, error }) => {
        setLoading(false)
        if (error || !data) {
          setInactive(true)
          return
        }
        setContext(data)
        const tz = data.availability_timezone
          ?? Intl.DateTimeFormat().resolvedOptions().timeZone
        setTimezone(tz)
        setWindows(windowsFromContext(data, tz))
      })
      .catch(() => {
        setLoading(false)
        setInactive(true)
      })
  }, [token])

  // Update a single window through a functional state read, so the recompute
  // always sees the latest draft even if input events ever batch.
  const updateWindow = (index: number, recompute: (w: WindowDraft) => WindowDraft) => {
    setWindows((prev) => prev.map((w, i) => (i === index ? recompute(w) : w)))
    setSubmitError(null)
  }

  const setStartDate = (index: number, date: string) => {
    updateWindow(index, (w) => {
      const time = timePart(w.start) || (date ? DEFAULT_START_TIME : '')
      const start = combine(date, time)
      return { start, end: reconcileEnd(w.start, start, w.end) }
    })
  }

  const setStartTime = (index: number, time: string) => {
    updateWindow(index, (w) => {
      const start = combine(datePart(w.start), time)
      return { start, end: reconcileEnd(w.start, start, w.end) }
    })
  }

  const setEndDate = (index: number, date: string) => {
    updateWindow(index, (w) => ({ ...w, end: combine(date, timePart(w.end)) }))
  }

  const setEndTime = (index: number, time: string) => {
    updateWindow(index, (w) => ({
      ...w,
      end: combine(datePart(w.end) || datePart(w.start), time),
    }))
  }

  const addWindow = () => {
    setWindows((prev) => (prev.length >= MAX_WINDOWS ? prev : [...prev, { start: '', end: '' }]))
  }

  const removeWindow = (index: number) => {
    setWindows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)))
    setSubmitError(null)
  }

  const canSubmit = windows.length >= 1
    && windows.length <= MAX_WINDOWS
    && windows.every(windowIsOrdered)
    && !windows.some(windowIsTooLong)
    && !submitting

  const handleSubmit = async () => {
    if (!token || !canSubmit) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      const { error } = await submitConciergeAvailabilityApiV1PublicConciergeTokenAvailabilityPost({
        path: { token },
        body: {
          timezone,
          windows: windows.map((w) => ({ start: w.start, end: w.end })),
        },
        baseURL: API_BASE_URL,
        throwOnError: false,
      })
      if (error) {
        setSubmitError(
          errorDetail(error)
            ?? 'Something went wrong saving your availability. Please try again.'
        )
        return
      }
      setSubmitted(true)
    } catch {
      setSubmitError('Something went wrong saving your availability. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-catch-white">
        <div className="flex items-center gap-3 text-gray-500">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-catch-beige-dark border-t-primary-500" />
          Loading…
        </div>
      </div>
    )
  }

  if (inactive || !context || !token) {
    return (
      <StatusCard title="This link is no longer active">
        <p>
          If you still need to get something scheduled, just reply on the
          email thread and Uta will pick it up.
        </p>
      </StatusCard>
    )
  }

  if (submitted) {
    return (
      <StatusCard title="Got it — Uta will take it from here" showCheck>
        <p>
          Your availability is in. Uta will line up a time that works and
          send the invite on the email thread.
        </p>
      </StatusCard>
    )
  }

  if (context.status === 'completed') {
    return (
      <StatusCard title="This meeting is already scheduled">
        <p>
          Nothing left to do here. If plans change, reply on the email
          thread and Uta will sort it out.
        </p>
      </StatusCard>
    )
  }

  if (context.status !== 'active') {
    return (
      <StatusCard title="This link is no longer active">
        <p>
          If you still need to get something scheduled, just reply on the
          email thread and Uta will pick it up.
        </p>
      </StatusCard>
    )
  }

  const editing = context.availability_windows.length > 0

  return (
    <div className="min-h-screen bg-catch-white py-6 px-4 sm:py-10">
      <div className="max-w-xl mx-auto animate-slide-up">
        <div className="bg-white rounded-3xl shadow-soft-lg border border-catch-beige overflow-hidden">
          <div className="p-6 sm:p-8 border-b border-catch-beige">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary-500/10">
                <CalendarClock className="w-6 h-6 text-primary-500" />
              </div>
              <div className="min-w-0">
                <h1 className="font-display text-2xl sm:text-[1.75rem] leading-tight text-catch-black [text-wrap:pretty]">
                  {context.subject
                    ? `Availability for “${context.subject}”`
                    : 'Share your availability'}
                </h1>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                  Uta, the scheduling assistant, is finding a time for{' '}
                  <span className="font-medium text-catch-black">{context.organizer_email}</span>.
                  {editing
                    ? ' You can adjust the times you shared earlier.'
                    : ' Add the times that work for you.'}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-7">
            <div>
              <label
                htmlFor="tz-select"
                className="flex items-center gap-1.5 text-sm font-medium text-catch-black mb-1.5"
              >
                <Globe className="w-4 h-4 text-gray-400" />
                Your timezone
              </label>
              <select
                id="tz-select"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className={FIELD_CLASS}
              >
                {timezoneOptions.map((tz) => (
                  <option key={tz} value={tz}>{formatTimezoneName(tz)}</option>
                ))}
              </select>
              <p className="mt-1.5 text-xs text-gray-400">
                All times below are read in this timezone.
              </p>
            </div>

            <div>
              <div className="text-sm font-medium text-catch-black mb-3">
                Times that work for you
              </div>
              <div className="space-y-3">
                {windows.map((window, index) => {
                  const outOfOrder = isComplete(window.start)
                    && isComplete(window.end)
                    && window.end <= window.start
                  const tooLong = windowIsTooLong(window)
                  const summary = formatWindowSummary(window.start, window.end)
                  return (
                    <div
                      key={index}
                      className="rounded-2xl border border-catch-beige bg-catch-white/60 p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                          Window {index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeWindow(index)}
                          disabled={windows.length <= 1}
                          aria-label={`Remove window ${index + 1}`}
                          className="p-1.5 rounded-lg text-gray-400 transition-colors hover:text-primary-500 hover:bg-primary-500/5 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="space-y-3">
                        <TimeEdge
                          label="Starts"
                          icon={<Calendar className="w-3.5 h-3.5" />}
                          date={datePart(window.start)}
                          time={timePart(window.start)}
                          minDate={minDateKey}
                          maxDate={maxDateKey}
                          onDate={(v) => setStartDate(index, v)}
                          onTime={(v) => setStartTime(index, v)}
                          ariaPrefix={`Window ${index + 1} start`}
                        />
                        <TimeEdge
                          label="Ends"
                          icon={<Clock className="w-3.5 h-3.5" />}
                          date={datePart(window.end)}
                          time={timePart(window.end)}
                          minDate={datePart(window.start) || minDateKey}
                          maxDate={maxDateKey}
                          onDate={(v) => setEndDate(index, v)}
                          onTime={(v) => setEndTime(index, v)}
                          ariaPrefix={`Window ${index + 1} end`}
                        />
                      </div>

                      {outOfOrder ? (
                        <p className="mt-3 text-xs font-medium text-primary-600">
                          The end needs to come after the start.
                        </p>
                      ) : tooLong ? (
                        <p className="mt-3 text-xs font-medium text-primary-600">
                          A window can be at most {MAX_WINDOW_HOURS} hours long.
                        </p>
                      ) : summary ? (
                        <p className="mt-3 text-xs text-gray-500">{summary}</p>
                      ) : null}
                    </div>
                  )
                })}
              </div>
              {windows.length < MAX_WINDOWS ? (
                <button
                  type="button"
                  onClick={addWindow}
                  className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-catch-beige-dark py-3 text-sm font-medium text-gray-500 transition-colors hover:border-primary-500 hover:text-primary-500"
                >
                  <Plus className="w-4 h-4" />
                  Add another window
                </button>
              ) : (
                <p className="mt-3 text-xs text-gray-400">
                  That&apos;s the limit — {MAX_WINDOWS} windows is plenty to work with.
                </p>
              )}
            </div>

            {submitError && (
              <p className="text-sm text-primary-600">{submitError}</p>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full h-12 rounded-xl text-base font-medium text-white bg-primary-500 shadow-sm transition-all hover:bg-primary-600 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {submitting
                ? 'Sending…'
                : editing ? 'Update availability' : 'Send availability'}
            </button>
          </div>
        </div>

        <PoweredByCatch />
      </div>
    </div>
  )
}

// One end of a window: a full-width date picker with the time picker beside it,
// labelled (Starts / Ends) so the day reads clearly on a narrow screen.
function TimeEdge({
  label,
  icon,
  date,
  time,
  minDate,
  maxDate,
  onDate,
  onTime,
  ariaPrefix,
}: {
  label: string,
  icon: React.ReactNode,
  date: string,
  time: string,
  minDate: string,
  maxDate: string,
  onDate: (value: string) => void,
  onTime: (value: string) => void,
  ariaPrefix: string,
}): JSX.Element {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5 text-xs font-medium text-gray-500">
        <span className="text-gray-400">{icon}</span>
        {label}
      </div>
      <div className="grid grid-cols-[1.4fr_1fr] gap-2">
        <input
          type="date"
          aria-label={`${ariaPrefix} date`}
          value={date}
          min={minDate}
          max={maxDate}
          onChange={(e) => onDate(e.target.value)}
          className={FIELD_CLASS}
        />
        <input
          type="time"
          aria-label={`${ariaPrefix} time`}
          value={time}
          onChange={(e) => onTime(e.target.value)}
          className={FIELD_CLASS}
        />
      </div>
    </div>
  )
}
